import { get } from "./FetchV2.util";

const STORAGE_HOST = "storage.googleapis.com";
const SIGNED_URL_TTL_MS = 30 * 60 * 1000;

const GCS_URI_SCHEME = "gs://";
/**
 * The fiscal-engine signs CFDI document links for 15 minutes. Caching them for
 * less, counted from when the client received them, keeps an expired link from
 * ever being served, regardless of clock skew between device and server.
 */
const CFDI_DOCUMENT_URL_TTL_MS = 10 * 60 * 1000;
const CFDI_DOCUMENT_FILENAME =
  /^([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})(.+)$/i;

type CfdiDocumentFiles = {
  xml_url?: string;
  pdf_url?: string;
  cancellation_xml_url?: string;
};

/**
 * Object key suffix → response field of `GET /cfdi/documents/{uuid}/files`.
 * Mirrors `documentKey` and `cancellationAcuseKey` in heru-fiscal-engine
 * (`internal/modules/cfdi/documentstorage.go`); the optional `test/` prefix
 * lives in the path, not in the filename, so it needs no entry.
 */
const CFDI_DOCUMENT_FIELD_BY_SUFFIX = new Map<string, keyof CfdiDocumentFiles>([
  [".pdf", "pdf_url"],
  [".xml", "xml_url"],
  ["_acuse_cancelacion.xml", "cancellation_xml_url"],
]);

type SignedUrlCacheEntry = { signedUrl: string; expiresAt: number };
type SignedUrlSource = { fetch: () => Promise<string>; ttlMs: number };

const signedUrlCache = new Map<string, SignedUrlCacheEntry>();
const inFlightRequests = new Map<string, Promise<string>>();

const extractKeyFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes(STORAGE_HOST)) return null;
    if (parsed.searchParams.has("X-Goog-Signature")) return null;
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    if (pathParts.length < 2) return null;
    return pathParts.slice(1).join("/");
  } catch {
    return null;
  }
};

const isGoogleStorageUrl = (url: string): boolean => {
  return url.includes(STORAGE_HOST);
};

const isGcsUri = (url: string): boolean => url.startsWith(GCS_URI_SCHEME);

const fetchSignedUrl = async (url: string, key: string): Promise<string> => {
  const data = await get<{ url?: string }>({
    service: "fiscalEngine",
    endpoint: `/files/signed-url?key=${encodeURIComponent(key)}`,
  });
  return data?.url || url;
};

/**
 * Resolves a `gs://` URI persisted by the fiscal-engine CFDI emission (the
 * private `heru-cfdi-docs-*` bucket) through its owner-checked files endpoint.
 * Rejects instead of falling back to the URI: a raw `gs://` is never openable.
 */
const fetchCfdiDocumentUrl = async (uri: string): Promise<string> => {
  const filename = uri.slice(uri.lastIndexOf("/") + 1);
  const [, uuid, suffix] = CFDI_DOCUMENT_FILENAME.exec(filename) ?? [];
  const field = suffix && CFDI_DOCUMENT_FIELD_BY_SUFFIX.get(suffix.toLowerCase());
  if (!uuid || !field) throw new Error("cfdi_document_uri_unsupported");

  const files = await get<CfdiDocumentFiles>({
    service: "fiscalEngine",
    endpoint: `/cfdi/documents/${encodeURIComponent(uuid)}/files`,
  });
  const signedUrl = files?.[field];
  if (!signedUrl) throw new Error(`cfdi_document_${field}_unavailable`);
  return signedUrl;
};

/**
 * A reference to a fiscal-engine CFDI document for surfaces that know its UUID
 * but not the stored URI (the Go QR request detail). Only `resolveUrl` reads
 * it, and it signs by the UUID and suffix in the filename, never by the bucket
 * or the path, so nothing is fetched until the user opens or downloads it.
 */
const cfdiDocumentUri = (uuid: string, kind: "pdf" | "xml"): string =>
  `${GCS_URI_SCHEME}cfdi-documents/${uuid}.${kind}`;

const getSignedUrlSource = (url: string): SignedUrlSource | null => {
  if (isGcsUri(url)) {
    return { fetch: () => fetchCfdiDocumentUrl(url), ttlMs: CFDI_DOCUMENT_URL_TTL_MS };
  }
  const key = extractKeyFromUrl(url);
  if (!key) return null;
  return { fetch: () => fetchSignedUrl(url, key), ttlMs: SIGNED_URL_TTL_MS };
};

const getSignedUrl = async (url: string): Promise<string> => {
  const source = getSignedUrlSource(url);
  if (!source) return url;

  const now = Date.now();
  const cached = signedUrlCache.get(url);
  if (cached && cached.expiresAt > now) return cached.signedUrl;

  const pending = inFlightRequests.get(url);
  if (pending) return pending;

  const request = source
    .fetch()
    .then((signedUrl) => {
      signedUrlCache.set(url, {
        signedUrl,
        expiresAt: Date.now() + source.ttlMs,
      });
      return signedUrl;
    })
    .finally(() => {
      inFlightRequests.delete(url);
    });

  inFlightRequests.set(url, request);
  return request;
};

const resolveUrl = async (url: string): Promise<string> => {
  if (!isGoogleStorageUrl(url) && !isGcsUri(url)) return url;
  return getSignedUrl(url);
};

/**
 * Fire-and-forget warm-up of the signed URL cache. Call it when the user
 * OPENS a surface with download actions (invoice preview, document card) so
 * the later download tap skips the signed-url API round trip — that round
 * trip is what makes the tap feel like seconds, especially inside the app
 * where it runs before handing off to the system browser.
 */
const prefetchSignedUrls = (
  urls: Array<string | null | undefined>,
): void => {
  for (const url of urls) {
    if (url) resolveUrl(url).catch(() => {});
  }
};

export {
  cfdiDocumentUri,
  getSignedUrl,
  resolveUrl,
  isGoogleStorageUrl,
  isGcsUri,
  prefetchSignedUrls,
};
